import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import { getPropertyTenantStatusLabel } from './screeningEnums';

const propertyTypeMap = {
  '1': 'Apartment',
  '2': 'House',
  '3': 'Townhouse',
  '4': 'Studio',
};

const formatScreeningDate = (createdAt) => {
  if (createdAt == null) return '—';
  const d = new Date(typeof createdAt === 'number' ? createdAt : Number(createdAt));
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

const transformScreening = (item) => {
  const landlord = item.landlord_screening_landlord_calls || {};
  const landlordName = [landlord.first_name, landlord.last_name].filter(Boolean).join(' ') || '—';
  const prop = item.property || item.Property || {};
  const propertyType = propertyTypeMap[String(prop.property_type)] || prop.property_type || '—';
  const propertyCode = prop.property_code ?? '—';
  const addressPart = prop.address || '';
  const unitPart = prop.unit_number ? `, ${prop.unit_number}` : '';
  const cityPart = prop.city ? ` ${prop.city}` : '';
  const propertyAddress = [addressPart + unitPart, cityPart].filter(Boolean).join(',').trim() || '—';
  // Final screening status: property_tenant_status 0:pending, 1:verified, 2:rejected
  const status = getPropertyTenantStatusLabel(
    item.property_tenant_status ?? item.property?.property_tenant_status ?? item.status
  );
  // Property id comes from the nested property object in the screening response
  const propertyId = (prop.id ?? prop.property_id ?? item.property_id) != null
    ? String(prop.id ?? prop.property_id ?? item.property_id)
    : null;
  return {
    id: item.id,
    propertyId,
    landlordName,
    propertyType,
    propertyCode,
    propertyAddress,
    screeningDate: formatScreeningDate(item.createdAt),
    status,
  };
};

const MyScreenings = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchScreenings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getApi('/screening/my-screenings');
        if (response?.statusCode === 200 && response?.data?.screenings) {
          const list = (response.data.screenings || []).map(transformScreening);
          setScreenings(list);
        } else {
          setScreenings([]);
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load screenings.');
        setScreenings([]);
        toast.error('Failed to load screenings.');
      } finally {
        setLoading(false);
      }
    };
    fetchScreenings();
  }, []);

  const filteredScreenings = screenings.filter((screening) =>
    screening.landlordName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    screening.propertyType.toLowerCase().includes(searchQuery.toLowerCase()) ||
    screening.propertyCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
    screening.propertyAddress.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusClass = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'verified') return 'screenings-status-verified';
    if (statusLower === 'rejected') return 'screenings-status-rejected';
    return 'screenings-status-pending';
  };

  const handleViewDetail = (screening) => {
    const id = screening.propertyId;
    if (id) {
      navigate(`/tenant/properties/${id}`);
    } else {
      toast.info('Property details are not available for this screening.');
      navigate(`/tenant/screenings/${screening.id}`);
    }
  };

  if (loading) {
    return (
      <div className="screenings-page">
        <div className="screenings-actions-bar">
          <div className="screenings-search-wrapper">
            <img src="/assets/images/SearchIcon.svg" alt="" className="screenings-search-icon" aria-hidden />
            <input
              type="text"
              className="screenings-search-input"
              placeholder="Search Properties"
              value=""
              readOnly
              disabled
              aria-label="Search (available when loaded)"
            />
          </div>
        </div>
        <h2 className="screenings-title">My Screenings</h2>
        <div className="screenings-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
          Loading screenings...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="screenings-page">
        <div className="screenings-content">
          <p style={{ color: 'var(--color-error, #dc3545)' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screenings-page">
      <div className="screenings-actions-bar">
        <div className="screenings-search-wrapper">
          <img 
            src="/assets/images/SearchIcon.svg" 
            alt="Search" 
            className="screenings-search-icon"
          />
          <input
            type="text"
            className="screenings-search-input"
            placeholder="Search Properties"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <h2 className="screenings-title">My Screenings ({filteredScreenings.length})</h2>
      
      <div className="screenings-content">
        {filteredScreenings.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '18px', color: '#666' }}>
            No screenings found.
          </div>
        ) : (
          <table className="screenings-table tenant-screenings-table">
            <thead>
              <tr>
                <th>Sr. no.</th>
                <th>Landlord Name</th>
                <th>Property Type</th>
                <th>Property Code</th>
                <th>Property Address</th>
                <th>Screening Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredScreenings.map((screening, index) => (
                <tr key={screening.id}>
                  <td>{index + 1}</td>
                  <td>{screening.landlordName}</td>
                  <td>{screening.propertyType}</td>
                  <td>{screening.propertyCode}</td>
                  <td>{screening.propertyAddress}</td>
                  <td>{screening.screeningDate}</td>
                  <td>
                    <span className={`screenings-status-pill ${getStatusClass(screening.status)}`}>
                      {screening.status}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="screenings-view-detail-button"
                      onClick={() => handleViewDetail(screening)}
                    >
                      View detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MyScreenings;
